import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import "../styles/callFloater.scss";

/**
 * Bottom-center pill that appears while a LiveKit call is running and
 * the AiTranscript modal is minimised. Body of the pill (icon + status
 * + timer) is a button — clicking it restores the modal. The red circle
 * on the right is a separate button that ends the call. Two nested
 * buttons would be invalid HTML, so they sit side-by-side with a
 * 1px hairline between.
 *
 * Icons + tokens taken verbatim from the Figma frame — the phone-outbound
 * glyph and the hangup glyph are the design's paths, offset into their
 * own viewBoxes so they render at native size inside their tiles.
 *
 * Mounted by ActiveCallProvider only when the call is connected AND the
 * modal is hidden; unmounts as soon as either condition flips.
 */
const formatElapsed = (startedAtMs, nowMs) => {
  const totalSecs = Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

/* Filled phone-outbound glyph. viewBox is the exact bounding box of the
   path in the Figma export, so no scaling maths — it draws at native
   13.5×13.5. */
const PhoneOutIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="55.25 41.25 13.5 13.5"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M68.0117 50.5312C68.2227 50.5312 68.3984 50.6016 68.5391 50.7422C68.6797 50.8828 68.75 51.0586 68.75 51.2695V53.8711C68.75 54.457 68.5039 54.75 68.0117 54.75C64.5195 54.75 61.5195 53.4961 59.0117 50.9883C56.5039 48.4805 55.25 45.4805 55.25 41.9883C55.25 41.4961 55.543 41.25 56.1289 41.25H58.7305C58.9414 41.25 59.1172 41.3203 59.2578 41.4609C59.3984 41.6016 59.4688 41.7773 59.4688 41.9883C59.4688 42.9023 59.6094 43.7812 59.8906 44.625C59.9844 44.9297 59.9258 45.1875 59.7148 45.3984L58.2734 46.6641C59.3516 48.9141 61.0742 50.6133 63.4414 51.7617L64.6016 50.2852C64.7422 50.1445 64.918 50.0742 65.1289 50.0742C65.2461 50.0742 65.3281 50.0859 65.375 50.1094C66.2188 50.3906 67.0977 50.5312 68.0117 50.5312Z" />
  </svg>
);

/* Hangup glyph — the phone tilted forward with two legs dipping. Native
   bounds 17×6.3, rendered at 18×8 for a comfortable target inside the
   40×40 red tile. */
const HangupIcon = () => (
  <svg
    width="18"
    height="8"
    viewBox="241 44 18 7"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M250 45.875C248.849 45.875 247.764 46.041 246.746 46.373V48.5645C246.746 48.8965 246.613 49.1178 246.348 49.2285C245.639 49.5605 245.008 49.9922 244.455 50.5234C244.322 50.6562 244.156 50.7227 243.957 50.7227C243.758 50.7227 243.592 50.6562 243.459 50.5234L241.699 48.7637C241.566 48.6309 241.5 48.4648 241.5 48.2656C241.5 48.0664 241.566 47.9004 241.699 47.7676C244.023 45.554 246.79 44.4473 250 44.4473C253.21 44.4473 255.977 45.554 258.301 47.7676C258.434 47.9004 258.5 48.0664 258.5 48.2656C258.5 48.4648 258.434 48.6309 258.301 48.7637L256.541 50.5234C256.408 50.6562 256.242 50.7227 256.043 50.7227C255.844 50.7227 255.678 50.6562 255.545 50.5234C255.058 50.0586 254.427 49.627 253.652 49.2285C253.387 49.1178 253.254 48.9076 253.254 48.5977V46.4062C252.147 46.0521 251.062 45.875 250 45.875Z" />
  </svg>
);

const CallFloater = ({ patientName, startedAt, onRestore, onHangup }) => {
  /* Tick once a second — cheap, and the timer text is the only thing
     that changes on the floater. Skip when startedAt is 0 (call not
     connected yet) so the initial render doesn't show "00:00". */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  const elapsed = startedAt ? formatElapsed(startedAt, now) : "00:00";
  const name = patientName || "Ongoing call";

  return (
    <div className="call-floater" role="region" aria-label="Ongoing call">
      <button
        type="button"
        className="cf-body"
        onClick={onRestore}
        title="Open transcript view"
        aria-label={`Open transcript view — call with ${name}, ${elapsed}`}
      >
        <span className="cf-icon" aria-hidden="true">
          <PhoneOutIcon />
        </span>
        <span className="cf-text">
          <span className="cf-name">
            <span className="cf-dot" aria-hidden="true" />
            {name}
          </span>
          <span className="cf-timer">{elapsed}</span>
        </span>
      </button>
      <span className="cf-sep" aria-hidden="true" />
      <button
        type="button"
        className="cf-hangup"
        onClick={onHangup}
        title="End call"
        aria-label="End call"
      >
        <HangupIcon />
      </button>
    </div>
  );
};

CallFloater.propTypes = {
  patientName: PropTypes.string,
  /** ms epoch of the moment callState first became "connected". */
  startedAt: PropTypes.number,
  onRestore: PropTypes.func.isRequired,
  onHangup: PropTypes.func.isRequired,
};

export default CallFloater;
